import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { withTranslation } from "react-i18next";
import { SwitchLanguages } from "../../components";
import classnames from "classnames";
import { ReactNotifications } from "core/logic";
import { Browser } from "core/utils/";
import { STORAGE_KEYS } from "core/utils/constants";
import "./style.scss";

/**
 * Component for showing OTP verification page.
 * 
 * @component
 */
@inject("authStore")
@observer
class ScreenOtp extends Component {

  constructor(props) {
    super(props);
    this.state = {
      otp: "",
      trustDevice: false,
      errors: {
        otp: false,
        hasError: () => {
          return this.state?.errors?.otp;
        }
      },
      isSubmitting: false,
      isResending: false,
      remainingSeconds: null,
      isExpired: false,
      resendWaitSeconds: 0,
      canResend: true
    };
    this.timerInterval = null;
    this.resendTimerInterval = null;
  }

  async componentDidMount() {
    const { authStore, history, i18n } = this.props;
    if (Browser.getSavedInfo(STORAGE_KEYS.SESSION_ID)) {
      // 本セッションIDがあればトップページへ
      history.push("/");
      return;
    }
    const sessionId = Browser.getSavedInfo(STORAGE_KEYS.PRESESSION_ID)
    if (!sessionId) {
      // 仮セッションIDが無ければログイン画面へ
      history.push("/login");
      return;
    }
    if (!authStore.otpData.tempSessionId) {
      // 前回のOTP情報の取得
      let success = await authStore.getOtpInfo();
      if (!success) {
        history.push("/login");
        return;
      }
    }

    // Set language based on OTP data
    const language = authStore.otpData.language;
    if (language === 0) {
      i18n.changeLanguage('jp');
    } else if (language === 1) {
      i18n.changeLanguage('en');
    }

    // Start countdown timer
    this.startCountdown(authStore.otpData.expiresIn);
    // Start cooldown if provided
    if (authStore.otpData.resendWaitSeconds) {
      this.startResendCooldown(authStore.otpData.resendWaitSeconds);
    }
  }

  componentWillUnmount() {
    // Clear timers on unmount
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    if (this.resendTimerInterval) {
      clearInterval(this.resendTimerInterval);
    }
  }

  /**
   * startCountdown
   * Start countdown timer
   * 
   * @param {number} seconds - Initial seconds for countdown
   * @returns null
   */
  startCountdown = (seconds) => {
    const { history } = this.props;
    // Clear existing timer if any
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    // Set initial remaining seconds
    this.setState({
      remainingSeconds: seconds,
      isExpired: false
    });

    // Start countdown
    this.timerInterval = setInterval(() => {
      this.setState(prevState => {
        const newRemaining = prevState.remainingSeconds - 1;

        if (newRemaining <= 0) {
          // Timer expired
          clearInterval(this.timerInterval);
          return {
            remainingSeconds: 0,
            isExpired: true
          };
        }

        return {
          remainingSeconds: newRemaining
        };
      });
      if (this.state.isExpired) {
        history.push("/login");
        return;
      }
    }, 1000);
  };

  /**
   * formatTime
   * Format seconds to MM:SS
   * 
   * @param {number} seconds - Seconds to format
   * @returns {string} Formatted time string
   */
  formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * onSubmit
   * handle verify button
   * 
   * @returns null
   */
  onSubmit = async () => {
    const { otp, trustDevice, errors, isSubmitting } = this.state;
    this.checkValidateRequired("otp", otp);

    if (otp && !errors.hasError() && !isSubmitting) {
      const { authStore, history, t } = this.props;
      this.setState({ isSubmitting: true });

      let success = false;
      try {
        success = await authStore.verifyOtp({ otp, trustDevice });
      } catch (error) {
        // Error is handled by ApiStore
      } finally {
        this.setState({ otp: "", isSubmitting: false });
      }
      if (success) {
        const fromPath = this.props.location.state?.from;
        if (fromPath && fromPath !== "/") {
          history.push(fromPath);
        } else {
          history.push("/");
        }
      }
    }
  };

  /**
   * startResendCooldown
   * Start resend cooldown timer
   * 
   * @param {number} seconds - Cooldown seconds
   * @returns null
   */
  startResendCooldown = (seconds) => {
    // Clear existing timer if any
    if (this.resendTimerInterval) {
      clearInterval(this.resendTimerInterval);
    }

    // Set initial cooldown
    this.setState({
      resendWaitSeconds: seconds,
      canResend: false
    });

    // Start cooldown
    this.resendTimerInterval = setInterval(() => {
      this.setState(prevState => {
        const newWait = prevState.resendWaitSeconds - 1;

        if (newWait <= 0) {
          // Cooldown expired
          clearInterval(this.resendTimerInterval);
          return {
            resendWaitSeconds: 0,
            canResend: true
          };
        }

        return {
          resendWaitSeconds: newWait
        };
      });
    }, 1000);
  };

  /**
   * onResend
   * handle resend button
   * 
   * @returns null
   */
  onResend = async () => {
    const { isResending, canResend } = this.state;
    const { authStore: { otpData: resendCount } } = this.props;
    if (isResending || !canResend) return;
    if (!resendCount || resendCount <= 0) return;

    const { authStore, t, history } = this.props;
    this.setState({ isResending: true });

    try {
      const result = await authStore.resendOtp();
      if (result && result.success) {
        ReactNotifications("info", t("otp.resend_success"), "");
        this.startCountdown(authStore.otpData.expiresIn);
        // Start cooldown if provided
        if (authStore.otpData.resendWaitSeconds) {
          this.startResendCooldown(authStore.otpData.resendWaitSeconds);
        }
      }
    } catch (error) {
      // Error is handled by ApiStore
    } finally {
      this.setState({ isResending: false });
    }
  };

  /**
   * onChange
   * handle OTP input changed and auto run validation
   * 
   * @param e {Object} : change event
   * @returns  null
   */
  onChange = e => {
    e.persist();
    const { value } = e.target;
    // Only allow digits and max 6 characters
    const sanitizedValue = value.replace(/\D/g, '').slice(0, 6);
    this.setState(
      {
        otp: sanitizedValue
      },
      () => this.checkValidateRequired("otp", sanitizedValue)
    );
  };

  /**
   * checkValidateRequired
   * validation for OTP
   * 
   * @param {string} name: name of the field
   * @param {string} value: value of the field
   * @returns  null
   */
  checkValidateRequired = (name, value) => {
    let errors = this.state.errors;
    if (!value && name) {
      errors[name] = true;
    } else if (value && value.length !== 6) {
      errors[name] = true;
    } else {
      errors[name] = false;
    }
    this.setState({
      errors
    });
  };

  /**
   * render
   * 
   * @return  {Component} 
   */
  render() {
    const { t, authStore } = this.props;
    const { otp, errors, isSubmitting, isResending, remainingSeconds, isExpired } = this.state;
    const { maskedUserId, resendCount } = authStore.otpData;

    return <div className="otp-page-wrapper">
      <div className={"login-page full-width"}>
        <div className="login-box">
          <div className="login-logo">
            <img style={{ width: "100%" }} src="/logo.png" alt="logo"></img>
          </div>
          <div className="login-box-body">
            <p style={{ color: "#3C3C3C", marginBottom: "15px", fontSize: "18px", fontWeight: "600", textAlign: "center" }}>
              {t("otp.title")}
            </p>
            <p style={{ color: "#666", fontSize: "14px", marginBottom: "10px", whiteSpace: "pre-line" }}>
              {t("otp.message")}
            </p>
            <p style={{ color: "#666", fontSize: "13px", marginBottom: "5px" }}>
              {t("otp.email_label")}
            </p>
            <p style={{ color: "#667eea", fontSize: "14px", fontWeight: "600", marginBottom: "15px" }}>
              {maskedUserId}
            </p>
            {remainingSeconds !== null && (
              <p style={{
                color: isExpired ? "#d32f2f" : (remainingSeconds < 60 ? "#ff9800" : "#999"),
                fontSize: "12px",
                marginBottom: "20px",
                fontWeight: isExpired || remainingSeconds < 60 ? "600" : "normal"
              }}>
                {isExpired ? t("otp.expired") : t("otp.expires_in_countdown", { time: this.formatTime(remainingSeconds) })}
              </p>
            )}
            <div
              className={classnames("form-group has-feedback", {
                error: errors["otp"]
              })}
            >
              <input
                name="otp"
                type="text"
                className="form-control"
                value={otp}
                onChange={this.onChange}
                maxLength="6"
                autoFocus
                style={{ textAlign: "center", fontSize: "20px", letterSpacing: "8px", padding: "0px" }}
              />
            </div>
            <div style={{ marginBottom: "15px", textAlign: "left" }}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "14px" }}>
                <input
                  type="checkbox"
                  checked={this.state.trustDevice}
                  onChange={(e) => this.setState({ trustDevice: e.target.checked })}
                  style={{ marginRight: "8px", cursor: "pointer" }}
                />
                <span style={{ color: "#666" }}>{t("otp.trust_device")}</span>
              </label>
            </div>
            <button
              className="btn btn-primary btn-lg"
              onClick={this.onSubmit}
              disabled={errors.hasError() || otp.length !== 6 || isSubmitting || isExpired}
              style={{ width: "100%", padding: "14px", fontSize: "16px", fontWeight: "600" }}
            >
              {isSubmitting ? "..." : t("otp.submit")}
            </button>
            <div className="row text-center" style={{ marginTop: "15px" }}>
              {!resendCount || resendCount <= 0 ? (
                <span style={{
                  color: "#999",
                  fontSize: "14px"
                }}>
                  {t("otp.resend_limit_exceeded")}
                </span>
              ) : this.state.resendWaitSeconds > 0 ? (
                <span style={{
                  color: "#999",
                  fontSize: "14px"
                }}>
                  {t("otp.resend_wait", { seconds: this.state.resendWaitSeconds })}
                </span>
              ) : (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    this.onResend();
                  }}
                  style={{
                    color: "#667eea",
                    textDecoration: "none",
                    fontSize: "14px",
                    cursor: (isResending || !this.state.canResend) ? "not-allowed" : "pointer",
                    opacity: (isResending || !this.state.canResend) ? 0.5 : 1
                  }}
                >
                  {isResending ? t("otp.resending") : t("otp.resend", { times: resendCount })}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="main-footer lang-switch-footer">
          <SwitchLanguages mode={0} />
        </div>
      </div>
    </div>
  }
}

export default withTranslation()(ScreenOtp);
